import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldInputProps, useFormikContext } from 'formik';
import { flattenDeep, uniq } from 'lodash';
import { FC, Fragment, InputHTMLAttributes, ReactElement } from 'react';
import { SimpleCheckbox } from 'src/components/form/checkbox';
import { ErrorMessage } from 'src/components/form/error-message';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { FormOption } from 'src/components/form/form-option';
import { Label } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { Hint } from 'src/components/hint';
import { Stack } from 'src/components/stack';
import { Tooltip } from 'src/components/tooltip';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useId } from 'src/hooks/use-id';

export type GroupedFormOption<T extends string> = {
  group: string;
  options: FormOption<T>[];
};

type CheckboxGroupProps = {
  label: string;
  name: string;
  options: FormOption<string>[] | GroupedFormOption<string>[];
  stacked?: boolean;
  stackedItems?: boolean;
  toggleAll?: boolean;
  hideLabel?: boolean;
  hint?: ReactElement | string;
  info?: ReactElement | string;
  isGroupFilter?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

export const CheckboxGroup: FC<CheckboxGroupProps> = (props) => {
  const { minTablet } = useBreakpoints();
  const {
    label,
    name,
    options,
    isGroupFilter = false,
    stacked = !minTablet,
    stackedItems = false,
    autoFocus,
    toggleAll = false,
    hideLabel = false,
    hint,
    info,
    ...inputProps
  } = props;

  const optionGroups = normalizeOptions(options);

  const id = useId();

  const { setFieldValue } = useFormikContext<any>();
  const [field, showError, error] = useCustomField<string[]>(name, label);

  const checks = optionGroups.map((optionGroup) =>
    optionGroup.options.map((option) =>
      field.value.some((value) => value === option.value)
    )
  );

  const allChecked = flattenDeep(checks).every((check) => check);

  const someChecked = flattenDeep(checks).some((check) => check);

  const toggleAllChecked = allChecked ? true : someChecked ? 'mixed' : false;

  const fieldItem = (
    <FieldItem>
      {/* div is needed for layout */}
      <div>
        <Stack gap={2.5} inline alignItems="start">
          {toggleAll && (
            <strong>
              <SimpleCheckbox
                showError={showError}
                label={allChecked ? 'Unselect all' : 'Select all'}
                checked={toggleAllChecked}
                onChange={(event) => {
                  if (event.currentTarget.checked) {
                    setFieldValue(
                      field.name,
                      flattenDeep(
                        optionGroups.map((optionGroup) =>
                          optionGroup.options.map((option) => option.value)
                        )
                      )
                    );
                  } else {
                    setFieldValue(field.name, []);
                  }
                }}
              />
            </strong>
          )}

          {optionGroups.map((optionGroup, index) => (
            <Fragment key={optionGroup.group}>
              <OptionGroup
                name={name}
                label={optionGroup.group}
                options={optionGroup.options}
                stackedItems={optionGroup.group ? true : stackedItems}
                field={field}
                showError={showError}
                id={index === 0 ? id : undefined}
                {...inputProps}
              />
            </Fragment>
          ))}
        </Stack>
      </div>

      {hint && (
        <Hint disabled={props.disabled} mode="formInput" children={hint} />
      )}
      {showError && error && !isGroupFilter && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
          type="selectable"
        />
      )}
    </FieldItem>
  );

  return hideLabel ? (
    fieldItem
  ) : (
    <FieldLayout stacked={stacked}>
      <Label htmlFor={id} error={showError}>
        {label}

        {info && (
          <>
            &nbsp;
            <Tooltip content={info}>
              {(targetProps) => (
                <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
              )}
            </Tooltip>
          </>
        )}
      </Label>

      {fieldItem}
    </FieldLayout>
  );
};

function normalizeOptions(
  options: FormOption<string>[] | GroupedFormOption<string>[]
): GroupedFormOption<string>[] {
  if (!options.length) return [{ group: '', options: [] }];

  if ((options[0] as GroupedFormOption<string>).group) return options as any;

  return [{ group: '', options: options as any }];
}

type OptionGroupProps = {
  name: string;
  label: string;
  options: FormOption<string>[];
  stackedItems?: boolean;
  field: FieldInputProps<string[]>;
  showError: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

const OptionGroup: FC<OptionGroupProps> = (props) => {
  const {
    label,
    name,
    options,
    stackedItems = false,
    field,
    showError,
    ...inputProps
  } = props;

  const { setFieldValue } = useFormikContext<any>();

  const childChecked = options.map((option) => {
    return Boolean(field.value.find((value) => value === option.value));
  });

  const allChildChecks = childChecked.every((check) => check);
  const someChildChecks = childChecked.some((check) => check);

  const toggleAllChildChecked = allChildChecks
    ? true
    : someChildChecks
    ? ('mixed' as const)
    : false;

  return (
    <div>
      <Stack
        gap={stackedItems ? 0.5 : 3.5} // same as radios
        inline
        alignItems="start"
        flow={stackedItems ? 'row' : 'column'}
      >
        {label && (
          <strong>
            <SimpleCheckbox
              label={label}
              data-testid={label}
              checked={toggleAllChildChecked}
              onChange={(event) => {
                if (event.currentTarget.checked) {
                  setFieldValue(
                    field.name,
                    uniq([...field.value, ...options.map(({ value }) => value)])
                  );
                } else {
                  setFieldValue(
                    field.name,
                    field.value.filter(
                      (value) =>
                        !options.some((option) => option.value === value)
                    )
                  );
                }
              }}
              showError={showError}
            />
          </strong>
        )}

        {options.map((option, index) => (
          <WrappedCheckbox
            key={option.value}
            option={option}
            checked={childChecked[index]}
            field={field}
            showError={showError}
            {...inputProps}
          />
        ))}
      </Stack>
    </div>
  );
};

type WrappedCheckboxProps = {
  option: FormOption<string>;
  checked?: boolean;
  field: FieldInputProps<string[]>;
  showError: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

const WrappedCheckbox: FC<WrappedCheckboxProps> = ({
  option,
  checked,
  field,
  showError,
  ...inputProps
}) => {
  const { setFieldValue } = useFormikContext<any>();
  return (
    <SimpleCheckbox
      label={option.label}
      data-testid={`${field.name}:${option.value}`}
      {...field}
      {...inputProps}
      data-value={option.value}
      value={option.value}
      checked={checked}
      onChange={(event) => {
        if (event.target.checked) {
          setFieldValue(field.name, [...field.value, option.value]);
        } else {
          const index = field.value.findIndex(
            (value) => value === option.value
          );
          setFieldValue(field.name, [
            ...field.value.slice(0, index),
            ...field.value.slice(index + 1),
          ]);
        }
      }}
      showError={showError}
    />
  );
};
